import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, ThemeProvider as MuiThemeProvider, CssBaseline, Typography } from '@mui/material';
import { lightTheme, darkTheme } from '@/utils/theme';
import ThemeProvider from '@/components/ThemeProvider/ThemeProvider';
import '@/styles/theme.css';
import { AuthService } from '@/services/authService';
import { User } from '@/models/user';

// Import layout
import DashboardLayout from '@/components/DashboardLayout/DashboardLayout';

// Import pages
import LoginPage from '@/pages/LoginPage/LoginPage';
import RegisterPage from '@/pages/RegisterPage/RegisterPage';
import HomePage from '@/pages/HomePage/HomePage';
import ManagementPage from '@/pages/ManagementPage/ManagementPage';
import CreateInvoicePage from '@/pages/CreateInvoicePage/CreateInvoicePage';
import EditInvoicePage from '@/pages/EditInvoicePage/EditInvoicePage';
import PrintInvoicePage from '@/pages/PrintInvoicePage/PrintInvoicePage';
import PrintInvoicePageNew from '@/pages/PrintInvoicePage/PrintInvoicePageNew';
import PrintInvoicePageTest from '@/pages/PrintInvoicePage/PrintInvoicePageTest';
import TestCalculations from '@/components/printPage/TestCalculations';
import { ComingSoon } from '@/components';
import ReportsPage from '@/pages/ReportsPage/ReportsPage';
import SettingsPage from '@/pages/SettingsPage/SettingsPage';


// Import management configurations
import { 
  customerManagementConfig, 
  productManagementConfig, 
  userManagementConfig,
  invoiceManagementConfig
} from '@/config/managementConfigs';

// Import components
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [loading, setLoading] = useState(true);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (AuthService.isAuthenticated()) {
          const storedUser = AuthService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            // Try to get user profile from server
            const response = await AuthService.getProfile();
            if (response.success) {
              setUser(response.data);
              AuthService.storeAuthData(AuthService.getToken()!, response.data);
            } else {
              AuthService.logout();
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        AuthService.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle logout
  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
  };

  // Handle login
  const handleLogin = (userData: User, token: string) => {
    AuthService.storeAuthData(token, userData);
    setUser(userData);
  };

  // Handle theme toggle
  const handleThemeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('darkMode') === null) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <MuiThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <ThemeProvider theme={darkMode ? 'dark' : 'light'}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh' }}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/home" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/register"
            element={
              user ? (
                <Navigate to="/home" replace />
              ) : (
                <RegisterPage />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/home"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Dashboard">
                  <HomePage
                    user={user}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={handleThemeToggle}
                  />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/customers"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Customers">
                  <ManagementPage
                    config={customerManagementConfig}
                    user={user}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={handleThemeToggle}
                  />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/products"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Products">
                  <ManagementPage
                    config={productManagementConfig}
                    user={user}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={handleThemeToggle}
                  />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/create-invoice"
            element={
              user ? (
                <DashboardLayout
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  setDarkMode={handleThemeToggle}
                  pageTitle="Invoice"
                  showBackButton={true}
                  onBack={() => navigate(-1 as any)}
                >
                  <CreateInvoicePage user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/edit-invoice/:id"
            element={
              user ? (
                <EditInvoicePage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Invoices">
                  <ManagementPage
                    config={invoiceManagementConfig}
                    user={user}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={handleThemeToggle}
                  />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/print/:id"
            element={
              user ? (
                <PrintInvoicePage
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  setDarkMode={handleThemeToggle}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/print-new/:id"
            element={
              user ? (
                <PrintInvoicePageNew
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  setDarkMode={handleThemeToggle}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/test-print"
            element={
              user ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>Print System Test</Typography>
                  <TestCalculations />
                </Box>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/print-test"
            element={
              user ? (
                <PrintInvoicePageTest
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  setDarkMode={handleThemeToggle}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/print-generic/:id"
            element={
              user ? (
                <ComingSoon 
                  title="Generic Invoice Printing"
                  description="Generic invoice printing functionality will be available in a future update."
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Reports">
                  <ReportsPage />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Settings">
                  <SettingsPage />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/users"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={handleThemeToggle} pageTitle="Users">
                  <ManagementPage
                    config={userManagementConfig}
                    user={user}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={handleThemeToggle}
                  />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Default routes */}
          <Route
            path="/"
            element={<Navigate to={user ? "/home" : "/login"} replace />}
          />
          <Route
            path="*"
            element={<Navigate to={user ? "/home" : "/login"} replace />}
          />
        </Routes>
      </Box>
      </ThemeProvider>
    </MuiThemeProvider>
  );
}

export default App; 